<i18n>
{
	"en": {
		"addToTrip": "Add to Trip",
		"newOrder": "New Order",
		"ordersFound": "No orders | {count} order | {count} orders",
		"addToTransporter": "Add to External",
		"noOrdersFound": "No orders were found that match this search. Try expanding your search criteria.",
		"failedToFetch": "Failed to get order details",
		"failedToGeocode": "Failed to geocode the selected orders",
		"packageLabel": "Labels",
		"waybill": "Waybill",
		"parallelSubOrderCreated": "Parallel created",
		"unableToCreateParallelSubOrder": "Unable to create parallel"
	},
	"no": {
		"addToTrip": "Legg på tur",
		"newOrder": "Nytt oppdrag",
		"ordersFound": "Ingen oppdrag | {count} oppdrag | {count} oppdrag",
		"addToTransporter": "Ekstern leverandør",
		"noOrdersFound": "Vi fant ingen oppdrag som matcher dette søket. Prøv å utvide søkekriteriene.",
		"failedToFetch": "Kunne ikke søke etter oppdrag detaljer",
		"failedToGeocode": "Kunne ikke geokode oppdrag",
		"packageLabel": "Etiketter",
		"waybill": "Fraktbrev",
		"parallelSubOrderCreated": "Parallell strekning laget",
		"unableToCreateParallelSubOrder": "Kunne ikke lage parallel strekning"
	}
}
</i18n>
<template>
	<t-vertical-panel class="order-list">
		<div slot="header" class="order-list-header">
			<t-icon-button
				v-if="!filtersVisible"
				icon="filter-open"
				color="blue"
				iconSize="sm"
				type="bp"
				style="margin-right: 5px; min-width: 32px;"
				@click="$emit('toggleFilters')"
			/>
			<t-icon-button
				v-if="!filtersVisible"
				icon="search"
				color="blue"
				iconSize="sm"
				type="bp"
				style="margin-right: 15px; min-width: 32px;"
				@click="$emit('search')"
			/>

			<t-panel-header icon="orders">
				{{ $tc('ordersFound', !ordersLoading && ordersFetching ? totals.orders : ordersFound) }}
			</t-panel-header>
			<div
				v-show="subHeaderWidthStage !== 'small' && ordersFound"
				class="order-list-summary"
			>
				<t-metric-details
					:value="displayedVolume"
					decimals="1"
					hide-pie-icon
					unit="m3"
				/>
				<t-metric-details
					:value="displayedWeight"
					hide-pie-icon
					unit="kg"
				/>
				<t-metric-details
					:value="displayedLoadingMeters"
					decimals="1"
					hide-pie-icon
					unit="lm"
				/>
				<t-metric-details
					:value="displayedTotalPrice"
					decimals="2"
					hide-pie-icon
					:unit="totals.baseCurrency"
				/>
			</div>
		</div>
		<div slot="sub-header" ref="subHeader" class="order-list-sub-header">
			<div
				class="sub-header-buttons"
			>
				<div class="left">
					<t-shrinkable-button
						:shrink="subHeaderWidthStage === 'medium' || subHeaderWidthStage === 'small'"

						:value="$t('newOrder')"
						appearance="minimal"

						type="bp"
						iconBefore="plus"

						@click="openNewOrderModal"
					/>
					<t-shrinkable-button
						:shrink="subHeaderWidthStage === 'medium' || subHeaderWidthStage === 'small'"

						:value="$t('packageLabel')"
						:disabled="rowsSelected === 0"
						appearance="minimal"

						type="bp"
						iconBefore="package-label"

						@click="showPackageLabel"
					/>
					<t-shrinkable-button
						:shrink="subHeaderWidthStage === 'medium' || subHeaderWidthStage === 'small'"

						:value="$t('waybill')"
						:disabled="rowsSelected === 0"
						appearance="minimal"

						type="bp"
						iconBefore="waybill"

						@click="showWaybill"
					/>
				</div>
				<div class="right">
					<t-shrinkable-button
						:shrink="subHeaderWidthStage === 'small'"
						:value="$t('addToTransporter')"
						:disabled="!addToTransporterEnabled"
						appearance="minimal"
						type="bp"
						iconBefore="new-link"
						@click="$emit('showAddToTransporter', {orders: selectedOrders, trip: tripDetails})"
					/>
					<t-shrinkable-button
						:shrink="subHeaderWidthStage === 'small'"
						:value="$t('addToTrip')"
						:disabled="!addToTripEnabled"
						appearance="minimal"
						type="bp"
						iconBefore="add-to-artifact"
						@click="$emit('showAddToTrip', {orders: selectedOrders, trip: tripDetails})"
					/>
				</div>
			</div>
		</div>

		<div class="order-list-content">
			<order-grid
				v-show="ordersFound"
				ref="grid"
				:orders="orders"
				:localStorageId="localStorageId"
				selectable
				@selected="onRowsSelected"
				@filtered="onFiltered"
				@geocode="onGeocodeOrder"
				@packageLabel="showSinglePackageLabel"
				@waybill="showSingleWaybill"
				@parallelSuborder="onCreateParallelSubOrder"
			/>

			<t-no-content-panel v-show="!ordersFound" :text="$t('noOrdersFound')" icon="order" />
			<t-overlay
				:isShown="ordersLoading"
				loaderMarginBottom="50px"
				absolute
				loader
			/>
		</div>
	</t-vertical-panel>
</template>

<script>
import OrderGrid from './OrderGrid';

import { eventBus, sumValues } from '@/core';
import {
	mapDynamicState, mapDynamicActions, mapDynamicGetters
} from '@/core/mapDynamic';
import TShrinkableButton from '@/components/evergreen/buttons/TShrinkableButton';
import TMetricDetails from '@/components/evergreen/timpex/layers/TMetricDetails';
import TIconButton from '@/components/evergreen/buttons/TIconButton';

export default {
	name: 'OrderSearchListGrid',
	components: { TIconButton, TMetricDetails, TShrinkableButton, OrderGrid },
	props: {
		filtersVisible: Boolean,
		availableWidth: Number,
	},
	data() {
		return {
			rowsSelected: 0,
		};
	},
	computed: {
		...mapDynamicState('computedStoreNamespace', [
			'orders',
			'ordersLoading',
			'ordersFetching',
			'totals'
		]),
		...mapDynamicState('computedLoadPlanningNamespace', ['filteredGridOrderIds']),
		...mapDynamicGetters('computedLoadPlanningNamespace', ['tripDetails']),
		subHeaderWidthStage() {

			let stage = 'large';

			switch (this.$i18n.locale) {

			case 'no':
			case 'nb-NO':

				if (this.availableWidth < 420) {
					stage = 'small';

				} else if (this.availableWidth < 651)
					stage = 'medium';

				break;
			case 'en':
			case 'en-US':
			case 'en-GB':

				if (this.availableWidth < 393)
					stage = 'small';

				else if (this.availableWidth < 593)
					stage = 'medium';

				break;
			default:
				stage = 'large';

			}

			return stage;
		},
		searchId() {
			return this.$route.params.searchId || -1;
		},
		computedStoreNamespace() {
			return `loadPlanning/${this.searchId}/orderSearch`;
		},
		computedLoadPlanningNamespace() {
			return `loadPlanning/${this.searchId}`;
		},
		localStorageId() {
			return `loadPlanningOrderSearchList/${this.searchId}`;
		},
		filteredOrders() {
			if (!this.filteredGridOrderIds || this.filteredGridOrderIds.length === 0) return this.orders;
			let lookup = new Set (this.filteredGridOrderIds);
			return this.orders.filter(x => lookup.has(x.id));
		},
		ordersFound() {
			return this.ordersLoading ? this.totals.orders : this.filteredOrders.length;
		},
		singleOrderSelected() {
			return this.rowsSelected === 1;
		},
		addToTransporterEnabled() {
			return this.rowsSelected > 0 && this.selectedOrders.every(x => !x.tripId && !x.external);
		},
		addToTripEnabled() {
			return this.rowsSelected > 0 && this.tripDetails && this.tripDetails.id && this.tripDetails.type.internalCode !== 'external' && this.selectedOrders.every(x => !x.tripId && !x.external);
		},
		selectedOrders() {
			if (this.rowsSelected > 0) {
				return this.$refs.grid.getSelectedItems();
			}
			return [];
		},
		subHeaderTinyLimit() { return this.filtersVisible ? 460 : 475; },
		totalVolume() {
			return this.ordersFetching ? this.totals.volume : sumValues(this.filteredOrders, 'volume');
		},
		totalWeight() {
			return this.ordersFetching ? this.totals.weight : sumValues(this.filteredOrders, 'grossWeight');
		},
		totalLoadingMeters() {
			return this.ordersFetching ? this.totals.loadingMeters : sumValues(this.filteredOrders, 'loadingMeters');
		},
		totalPrice() {
			return this.ordersFetching ? this.totals.totalPrice : sumValues(this.orders, 'totalPrice');
		},
		displayedVolume() {
			return this.rowsSelected > 0 ? sumValues(this.selectedOrders, 'volume') : this.totalVolume;
		},
		displayedWeight() {
			return this.rowsSelected > 0 ? sumValues(this.selectedOrders, 'grossWeight') : this.totalWeight;
		},
		displayedLoadingMeters() {
			return this.rowsSelected > 0 ? sumValues(this.selectedOrders, 'loadingMeters') : this.totalLoadingMeters;
		},
		displayedTotalPrice() {
			return this.rowsSelected > 0 ? sumValues(this.selectedOrders, 'totalPrice') : this.totalPrice;
		},
	},

	methods: {
		...mapDynamicActions('computedStoreNamespace', [ 'geocodeOrder', 'createParallelSubOrder' ]),
		...mapDynamicActions('computedLoadPlanningNamespace', [
			'setFilteredGridOrderIds',
			'getWaybill',
			'getPackageLabel',
		]),
		toggleFilters() {
			this.$emit('toggleFilters');
		},
		onRowsSelected(ids) {
			this.rowsSelected = ids.length;
		},
		onFiltered(ids) {
			this.setFilteredGridOrderIds(ids);
		},
		openNewOrderModal() {
			eventBus.$emit('toggleNewOrderModal',true);
		},
		async onGeocodeOrder(order) {
			try {
				await this.geocodeOrder(order);
			} catch (e) {
				this.$notify({
					type: 'error',
					title: this.$t('failedToGeocode')
				});
			}
		},
		showWaybill() {
			let ids = this.selectedOrders.map(x => x.orderId);
			this.getWaybill(ids);
		},
		showSingleWaybill(id) {
			this.getWaybill([id]);
		},
		showPackageLabel() {
			let ids = this.selectedOrders.map(x => x.orderId);
			this.getPackageLabel(ids);
		},
		showSinglePackageLabel(id) {
			this.getPackageLabel([id]);
		},
		async onCreateParallelSubOrder(subOrderId) {
			try {
				await this.createParallelSubOrder(subOrderId);
				this.$notify({
					type: 'success',
					title: this.$t('parallelSubOrderCreated'),
				});
			} catch (e) {
				this.$notify({
					type: 'error',
					title: this.$t('unableToCreateParallelSubOrder'),
					duration: -1
				});
			}
		},
	}
};
</script>


<style lang="scss" scoped>
.order-list {
	.order-list-header {
		height: 100%;
		display:flex;
		align-items: center;
		padding-left: 15px;
	}
	.order-list-summary {
		display:grid;
		padding: 15px;
		grid-template-columns: repeat(4, max-content);
		grid-column-gap: 10px;
		margin-right: auto;
	}
	.order-list-sub-header {
		height: 100%;
		padding: 0px 10px;

		.sub-header-buttons {

			height: 100%;
			display: flex;
			justify-content: space-between;
			align-items: center;

			.left {
				display: flex;

				button {
					margin-right: 8px;
				}
			}
			.right {
				display: flex;

				button {
					margin-left: 8px;
				}
			}
		}
	}
	.order-list-content {
		height: 100%;
		display: flex;
		align-items: center;
		justify-content: center;
		padding: 0px 5px
	}
}
</style>
